import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { APP_MODULES, COLOR_PALETTE, findActiveModule, filterVisibleModules } from "../config/appModules";
import api from "../lib/api";
import { useEntorno, RiseTag, RISE_GREEN, RISE_GREEN_DARK, RISE_BAR_H } from "./RiseBanner";   // v101.159.0
import UserMenu from "./UserMenu";
import BranchSelector from "./BranchSelector";
import { LayoutGrid, ChevronDown, Bell, User as UserIcon, Sun, Moon, HelpCircle } from "lucide-react";

/**
 * ModuleTopBar — barra superior estilo Odoo
 * ────────────────────────────────────────────────────
 * Reemplaza al sidebar lateral. Muestra:
 *  1. Botón "Aplicaciones" (LayoutGrid) → vuelve al launcher
 *  2. Nombre del módulo activo + dropdown con submenús
 *  3. Cambio rápido a otros módulos (segundo dropdown "Todas las apps")
 *  4. Notificaciones, sucursal, perfil, avatar
 */
export default function ModuleTopBar({ title, subtitle, actions }) {
  const { user, hasPerm } = useAuth();
  const { theme, toggle: toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [appsMenuOpen, setAppsMenuOpen] = useState(false);

  const notifRef = useRef(null);
  const submenuRef = useRef(null);
  const appsMenuRef = useRef(null);

  const activeModule = findActiveModule(location.pathname);
  const color = activeModule ? COLOR_PALETTE[activeModule.color] || COLOR_PALETTE.slate : COLOR_PALETTE.slate;
  const visibleModules = filterVisibleModules(APP_MODULES, user, hasPerm);

  // ── Notificaciones ─────────────────────
  const loadNotifications = async () => {
    if (!user) return;
    setNotifLoading(true);
    try {
      const { data } = await api.get("/v1/notifications/mine", { params: { limit: 5 } });
      setNotifs(data.items || []);
    } catch {
      setNotifs([]);
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    if (notifOpen) loadNotifications();
  }, [notifOpen]);

  // ── Click-outside handlers ─────────────
  useEffect(() => {
    const onClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (submenuRef.current && !submenuRef.current.contains(e.target)) setSubmenuOpen(false);
      if (appsMenuRef.current && !appsMenuRef.current.contains(e.target)) setAppsMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Cerrar submenús al cambiar de ruta
  useEffect(() => {
    setSubmenuOpen(false);
    setAppsMenuOpen(false);
  }, [location.pathname]);

  // Visible submenu items filtered by permissions
  const visibleSubmenu = (activeModule?.submenu || []).filter((s) => {
    if (s.perm && !hasPerm(s.perm)) return false;
    if (s.roles && !s.roles.includes(user?.role)) return false;
    return true;
  });

  const _rise = !!useEntorno()?.rise;   // v101.159.0 · RISE: barra verde, debajo de la franja RISE
  return (
    <header
      className="sticky z-[2000] border-b"
      style={{
        top: _rise ? RISE_BAR_H : 0,
        background: _rise
          ? `linear-gradient(180deg, ${RISE_GREEN} 0%, ${RISE_GREEN_DARK} 100%)`
          : "linear-gradient(180deg, rgba(5,21,29,0.92) 0%, rgba(2,11,16,0.9) 100%)",
        borderColor: "var(--border-strong)",
        backdropFilter: "blur(20px) saturate(150%)",
        WebkitBackdropFilter: "blur(20px) saturate(150%)",
        boxShadow: "0 1px 0 0 var(--secondary-glow), 0 10px 40px -20px rgba(26, 35, 126, 0.15)",
      }}
      data-testid="module-topbar"
    >
      {/* Neon accent line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
        style={{
          background: "linear-gradient(90deg, transparent 0%, var(--secondary) 25%, var(--accent) 50%, var(--secondary) 75%, transparent 100%)",
          opacity: 0.6,
        }}
      />
      <div className="px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 relative">
        {/* ═════ Izquierda: Apps + módulo activo ═════ */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {/* Botón Apps → launcher */}
          <button
            onClick={() => navigate("/apps")}
            className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-white transition group border border-transparent hover:border-[var(--secondary)]"
            style={{ borderRadius: "2px" }}
            title="Ver todas las aplicaciones"
            data-testid="topbar-apps-btn"
          >
            <LayoutGrid size={18} className="text-[var(--accent)] group-hover:scale-110 transition-transform" style={{ filter: "drop-shadow(0 0 6px var(--accent-glow))" }} />
            <span className="hidden md:inline text-[10px] font-semibold uppercase tracking-[0.2em] font-mono text-gray-200">
              Apps
            </span>
          </button>

          <RiseTag className="shrink-0" />
          <span className="w-px h-6" style={{ background: "var(--border)" }} />

          {/* Nombre del módulo activo + submenu dropdown */}
          {activeModule && (
            <div className="relative min-w-0" ref={submenuRef}>
              <button
                onClick={() => setSubmenuOpen((v) => !v)}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/10 transition group min-w-0"
                data-testid="module-menu-btn"
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: color.bg }}
                >
                  <activeModule.icon size={16} style={{ color: color.solid }} strokeWidth={2} />
                </div>
                <div className="text-left leading-tight min-w-0">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#E0E621]/80">
                    {activeModule.group}
                  </div>
                  <div className="font-bold text-white text-sm tracking-tight truncate flex items-center gap-1" style={{ fontFamily: "Space Grotesk" }}>
                    {activeModule.name}
                    {visibleSubmenu.length > 0 && <ChevronDown size={14} className={`transition-transform text-gray-300 ${submenuOpen ? "rotate-180" : ""}`} />}
                  </div>
                </div>
              </button>

              {/* Dropdown submenu · theme-aware v2.9 */}
              {submenuOpen && visibleSubmenu.length > 0 && (
                <div
                  className="absolute left-0 top-full mt-1 min-w-[240px] rounded-xl shadow-2xl overflow-hidden z-40"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-strong)",
                    boxShadow: "0 20px 40px -8px rgba(0,0,0,0.35), 0 0 0 1px var(--border-strong)",
                    backdropFilter: "blur(20px) saturate(160%)",
                  }}
                  data-testid="module-submenu"
                >
                  <div
                    className="px-4 py-2 border-b"
                    style={{
                      background: "var(--bg-elevated)",
                      borderColor: "var(--border)",
                    }}
                  >
                    <div className="text-[9px] font-mono uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
                      {activeModule.name}
                    </div>
                  </div>
                  {visibleSubmenu.map((s) => (
                    <NavLink
                      key={s.to}
                      to={s.to}
                      end
                      className={({ isActive }) =>
                        `block px-4 py-2.5 text-sm transition submenu-item ${isActive ? "submenu-item-active" : ""}`
                      }
                      data-testid={`submenu-${s.to.replace(/\//g, "-").slice(1)}`}
                    >
                      {s.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Título / subtítulo de la página (respaldo) */}
          {!activeModule && title && (
            <div className="ml-2 leading-tight">
              {subtitle && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#E0E621]/80">{subtitle}</div>
              )}
              <div className="font-bold text-white text-sm" style={{ fontFamily: "Space Grotesk" }}>
                {title}
              </div>
            </div>
          )}
        </div>

        {/* ═════ Derecha: actions + branch + notif + perfil ═════ */}
        <div className="flex items-center gap-1.5">
          {/* Selector rápido de todas las apps */}
          <div className="relative" ref={appsMenuRef}>
            <button
              onClick={() => setAppsMenuOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-white/10 text-gray-200 text-xs font-semibold uppercase tracking-wider font-mono transition"
              data-testid="quick-apps-btn"
              title="Módulos"
            >
              <LayoutGrid size={14} />
              {/* v101.94.0 · con zoom (pantalla más angosta) queda solo el ícono, el menú sigue disponible */}
              <span className="hidden lg:inline">Módulos</span>
              <ChevronDown size={12} className={`transition-transform ${appsMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {appsMenuOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-72 max-h-[70vh] overflow-y-auto rounded-xl shadow-2xl overflow-hidden z-40"
                style={{
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border-strong)",
                  boxShadow: "0 20px 40px -8px rgba(0,0,0,0.35), 0 0 0 1px var(--border-strong)",
                  backdropFilter: "blur(20px) saturate(160%)",
                }}
                data-testid="quick-apps-menu"
              >
                <div
                  className="px-4 py-2 border-b sticky top-0"
                  style={{ background: "var(--bg-elevated)", borderColor: "var(--border)" }}
                >
                  <div className="text-[9px] font-mono uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
                    Cambiar de aplicación
                  </div>
                </div>
                <div className="p-2 grid grid-cols-2 gap-1.5">
                  {visibleModules.map((m) => {
                    const c = COLOR_PALETTE[m.color] || COLOR_PALETTE.slate;
                    const Icon = m.icon;
                    const isActive = activeModule?.id === m.id;
                    return (
                      <button
                        key={m.id}
                        onClick={() => {
                          navigate(m.to);
                          setAppsMenuOpen(false);
                        }}
                        className={`flex flex-col items-center gap-1.5 p-2.5 rounded-lg transition submenu-item ${
                          isActive ? "submenu-item-active" : ""
                        }`}
                        data-testid={`quick-app-${m.id}`}
                      >
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center"
                          style={{ background: isActive ? "rgba(255,255,255,0.15)" : c.bg }}
                        >
                          <Icon size={16} style={{ color: isActive ? "#fff" : c.solid }} strokeWidth={2} />
                        </div>
                        <span className="text-[10px] font-semibold text-center leading-tight">
                          {m.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {actions && <div className="flex items-center gap-2">{actions}</div>}
          <BranchSelector />

          {/* Notificaciones */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setNotifOpen((v) => !v)}
              className="p-2 rounded-lg hover:bg-white/10 text-gray-200 transition relative"
              title="Notificaciones"
              data-testid="notifications-btn"
            >
              <Bell size={18} />
              {notifs.length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#E0E621] ring-2 ring-[#0A3747]" />
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50" data-testid="notifications-dropdown">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                  <span className="font-semibold text-sm text-[#0A3747]">Notificaciones</span>
                  <button
                    onClick={() => {
                      navigate("/admin/notificaciones");
                      setNotifOpen(false);
                    }}
                    className="text-[10px] uppercase font-mono text-emerald-600 hover:underline"
                  >
                    Ver todas
                  </button>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifLoading ? (
                    <div className="px-4 py-6 text-center text-xs text-gray-400">Cargando...</div>
                  ) : notifs.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <Bell size={24} className="text-gray-300 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">No hay notificaciones nuevas</p>
                    </div>
                  ) : (
                    notifs.map((n) => (
                      <div key={n.id} className="px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="text-[10px] font-mono uppercase text-emerald-700 font-semibold">
                            {n.notification_type || n.type || "info"}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            {n.queued_at ? new Date(n.queued_at).toLocaleDateString("es-EC") : ""}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700 line-clamp-2">
                          {n.subject || n.to_email || "Notificación"}
                        </p>
                        <span
                          className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded font-mono ${
                            n.status === "sent"
                              ? "bg-emerald-100 text-emerald-700"
                              : n.status === "failed"
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {n.status || "queued"}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Perfil rápido */}
          <button
            onClick={() => navigate("/perfil")}
            className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-[var(--accent)] hover:text-[var(--accent-fg)] text-white border border-white/10 transition text-[10px] font-semibold uppercase tracking-[0.2em] font-mono"
            style={{ borderRadius: "2px" }}
            title="Mi Perfil"
            data-testid="topbar-profile-btn"
          >
            <UserIcon size={14} />
            <span>Perfil</span>
          </button>

          {/* Ayuda · Centro de Documentación AlfaNet */}
          <button
            onClick={() => navigate("/centro-documentacion")}
            className="flex items-center justify-center p-2 bg-white/5 hover:bg-[var(--accent)] hover:text-[var(--accent-fg)] text-white border border-white/10 transition group"
            style={{ borderRadius: "2px" }}
            title="Centro de Documentación · Ayuda"
            aria-label="Abrir Centro de Documentación AlfaNet"
            data-testid="topbar-help-btn"
          >
            <HelpCircle
              size={16}
              className="group-hover:scale-110 transition-transform"
              style={{ filter: "drop-shadow(0 0 6px var(--accent-glow))" }}
            />
          </button>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 border border-white/10 hover:border-[var(--secondary)] text-gray-200 transition group"
            style={{ borderRadius: "2px", boxShadow: "inset 0 0 0 0 var(--secondary-glow)" }}
            title={isDark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
            data-testid="theme-toggle"
          >
            {isDark ? (
              <Sun size={16} className="text-[var(--accent)] group-hover:rotate-45 transition-transform" style={{ filter: "drop-shadow(0 0 6px var(--accent-glow))" }} />
            ) : (
              <Moon size={16} className="text-[var(--secondary)] group-hover:-rotate-12 transition-transform" style={{ filter: "drop-shadow(0 0 6px var(--secondary-glow))" }} />
            )}
          </button>

          <UserMenu darkTopbar />
        </div>
      </div>
    </header>
  );
}
